/**
 * FeedbackButton - Thumbs up/down feedback for tool executions.
 *
 * Allows users to rate tool executions with:
 * - Thumbs up (positive)
 * - Thumbs down (negative) with optional text/audio feedback
 *
 * Feedback is stored and used to improve future tool behavior.
 */

import { useState } from 'react'
import { ThumbsUp, ThumbsDown, Mic, MicOff, Send, X, Loader2 } from 'lucide-react'
import { submitFeedback, submitAudioFeedback } from '@/api/feedback'
import { useAudioRecording } from '@/hooks/useAudioRecording'

interface FeedbackButtonProps {
  auditLogId: number | null | undefined
  toolName: string
  onFeedbackSubmitted?: (rating: number) => void
}

type FeedbackState = 'idle' | 'expanded' | 'recording' | 'submitted'

export function FeedbackButton({
  auditLogId,
  toolName,
  onFeedbackSubmitted,
}: FeedbackButtonProps) {
  const [state, setState] = useState<FeedbackState>('idle')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [rating, setRating] = useState<number | null>(null)
  const [textFeedback, setTextFeedback] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submittedRating, setSubmittedRating] = useState<number | null>(null)

  const {
    isRecording,
    audioBlob,
    startRecording,
    stopRecording,
    clearRecording,
  } = useAudioRecording()

  // Don't render if no audit_log_id
  if (!auditLogId) {
    return null
  }

  const handleThumbsUp = async () => {
    if (isSubmitting || state === 'submitted') return

    setIsSubmitting(true)
    setRating(1)

    try {
      const result = await submitFeedback({
        audit_log_id: auditLogId,
        rating: 1,
      })

      if (result.ok) {
        setSubmittedRating(1)
        setState('submitted')
        onFeedbackSubmitted?.(1)
      } else {
        setError(result.error || 'Failed to submit feedback')
      }
    } catch (e) {
      setError('Failed to submit feedback')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleThumbsDown = () => {
    if (isSubmitting || state === 'submitted') return
    setRating(-1)
    setState('expanded')
  }

  const handleSubmitNegative = async () => {
    if (isSubmitting) return
    setIsSubmitting(true)

    try {
      let result

      if (audioBlob) {
        // Submit with audio
        result = await submitAudioFeedback(audioBlob, auditLogId, -1, textFeedback)
      } else {
        // Submit text only
        result = await submitFeedback({
          audit_log_id: auditLogId,
          rating: -1,
          text_feedback: textFeedback || undefined,
        })
      }

      if (result.ok) {
        setSubmittedRating(-1)
        setState('submitted')
        clearRecording()
        onFeedbackSubmitted?.(-1)
      } else {
        setError(result.error || 'Failed to submit feedback')
      }
    } catch (e) {
      setError('Failed to submit feedback')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    setState('idle')
    setRating(null)
    setTextFeedback('')
    setError(null)
    clearRecording()
  }

  const toggleRecording = async () => {
    if (isRecording) {
      await stopRecording()
    } else {
      await startRecording()
    }
  }

  // Submitted state - show thank you
  if (state === 'submitted') {
    return (
      <div className="inline-flex items-center gap-2 text-xs text-text-muted">
        {submittedRating === 1 ? (
          <ThumbsUp className="w-3 h-3 text-green-500" />
        ) : (
          <ThumbsDown className="w-3 h-3 text-red-400" />
        )}
        <span>Thanks for the feedback!</span>
      </div>
    )
  }

  // Expanded state - text/audio input for negative feedback
  if (state === 'expanded' || state === 'recording') {
    return (
      <div className="mt-2 space-y-2">
        <div className="flex items-center gap-2 text-xs text-text-muted">
          <ThumbsDown className="w-3 h-3 text-red-400" />
          <span>What went wrong?</span>
        </div>

        <div className="flex gap-2">
          <input
            type="text"
            value={textFeedback}
            onChange={(e) => setTextFeedback(e.target.value)}
            placeholder="Optional: describe the issue..."
            className="
              flex-1 px-3 py-1.5 rounded-lg text-sm
              bg-surface-hover border border-surface-border
              text-text placeholder-text-muted
              focus:outline-none focus:border-accent
            "
          />

          {/* Audio recording button */}
          <button
            onClick={toggleRecording}
            className={`
              p-2 rounded-lg transition-colors
              ${
                isRecording
                  ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                  : 'bg-surface-hover text-text-secondary hover:bg-surface-border hover:text-text'
              }
            `}
            title={isRecording ? 'Stop recording' : 'Record voice feedback'}
          >
            {isRecording ? (
              <MicOff className="w-4 h-4 animate-pulse" />
            ) : (
              <Mic className="w-4 h-4" />
            )}
          </button>

          {/* Submit button */}
          <button
            onClick={handleSubmitNegative}
            disabled={isSubmitting}
            className="
              p-2 rounded-lg
              bg-accent/20 text-accent
              hover:bg-accent/30
              disabled:opacity-50 disabled:cursor-not-allowed
              transition-colors
            "
            title="Submit feedback"
          >
            {isSubmitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </button>

          {/* Cancel button */}
          <button
            onClick={handleCancel}
            className="
              p-2 rounded-lg
              bg-surface-hover text-text-muted
              hover:bg-surface-border hover:text-text
              transition-colors
            "
            title="Cancel"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Show recording indicator */}
        {audioBlob && !isRecording && (
          <div className="flex items-center gap-2 text-xs text-green-400">
            <Mic className="w-3 h-3" />
            <span>Audio recorded</span>
            <button
              onClick={clearRecording}
              className="text-text-muted hover:text-red-400"
            >
              (clear)
            </button>
          </div>
        )}

        {error && <div className="text-xs text-red-400">{error}</div>}
      </div>
    )
  }

  // Idle state - show thumbs buttons
  return (
    <div className="inline-flex items-center gap-1">
      <button
        onClick={handleThumbsUp}
        disabled={isSubmitting}
        className="
          p-1.5 rounded-md
          text-text-muted hover:text-green-500 hover:bg-green-500/10
          disabled:opacity-50 disabled:cursor-not-allowed
          transition-colors
        "
        title={`Rate ${toolName} as helpful`}
      >
        {isSubmitting && rating === 1 ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <ThumbsUp className="w-3.5 h-3.5" />
        )}
      </button>

      <button
        onClick={handleThumbsDown}
        disabled={isSubmitting}
        className="
          p-1.5 rounded-md
          text-text-muted hover:text-red-400 hover:bg-red-400/10
          disabled:opacity-50 disabled:cursor-not-allowed
          transition-colors
        "
        title={`Report issue with ${toolName}`}
      >
        <ThumbsDown className="w-3.5 h-3.5" />
      </button>
    </div>
  )
}

export default FeedbackButton
